import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AvailableModals, closeModal } from './modalSlice';
import { ReduxState, useAppDispatch } from '../../store';
import { useGetCalculationQuery } from '../CalculationDetail/apiCalculationDetail';
import {
  PatchCalculationPayload,
  usePatchCalculationMutation,
} from '../Calculations/apiCalculations';
import { CalculationType } from '@ydistri/api-sdk';
import { Button, FormattedDatePicker, Text, Title } from '@ydistri/ds';
import { Alert, Form, Modal } from 'antd';
import dayjs from 'dayjs';
import { DateTimeFormats, YPICK_LABEL } from '../../components/global/_constants';
import { styled } from 'styled-components';

const TdWithPadding = styled.td`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

const modalId = AvailableModals.UNSET_PRODUCTION;

const UnsetProductionModal: React.FC = () => {
  const [form] = Form.useForm<{
    finishExecutionUntil: dayjs.Dayjs;
  }>();
  const dispatch = useAppDispatch();

  const open = useSelector((state: ReduxState) => state.modal.openedModal === modalId);
  const modalData = useSelector((state: ReduxState) => state.modal.modalData[modalId]);
  const calculationId = modalData?.calculationId ?? 0;

  const { data: calculationData } = useGetCalculationQuery(calculationId);
  const [editCalculation, { isLoading }] = usePatchCalculationMutation();

  const handleClose = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const initialValues = useMemo(
    () => ({
      finishExecutionUntil: dayjs(calculationData?.finishExecutionUntil), // dayjs().add(14, 'day'),
    }),
    [calculationData?.finishExecutionUntil],
  );

  useEffect(() => {
    form.setFieldValue(
      'finishExecutionUntil',
      calculationData?.finishExecutionUntil ? dayjs(calculationData.finishExecutionUntil) : null,
    );
  }, [calculationData?.finishExecutionUntil, form]);

  const removeProductionHandler = useCallback(() => {
    const payload: PatchCalculationPayload = {
      calculationId: calculationId,
      data: {
        calculationType: CalculationType.Simulation,
        finishExecutionUntil: undefined,
      },
    };
    editCalculation(payload).then(() => {
      handleClose();
    });
  }, [calculationId, editCalculation, handleClose]);

  const setExecutionDateOnlyHandler = useCallback(() => {
    form
      .validateFields()
      .then(values => {
        const payload: PatchCalculationPayload = {
          calculationId: calculationId,
          data: {
            finishExecutionUntil: values.finishExecutionUntil.format(DateTimeFormats.API_DATE),
          },
        };
        editCalculation(payload).then(() => {
          handleClose();
        });
      })
      .catch((error: unknown) => {
        // eslint-disable-next-line no-console -- we want the output
        console.log('Validation Error:', error);
      });
  }, [calculationId, editCalculation, form, handleClose]);

  if (!open) return null;
  return (
    <Modal title="" open={open} onCancel={handleClose} width={640} footer={null}>
      <table>
        <tbody>
          <tr>
            <TdWithPadding colSpan={2}>
              <Title>Change execution date</Title>
            </TdWithPadding>
          </tr>
          <tr>
            <td>
              <Form
                form={form}
                initialValues={initialValues}
                data-type="unset-production-form"
                name="unset_production_modal"
                layout="horizontal"
              >
                <Form.Item name="finishExecutionUntil" label="Final execution date: ">
                  <FormattedDatePicker />
                </Form.Item>
              </Form>
            </td>
            <td>
              <Button onClick={setExecutionDateOnlyHandler} disabled={isLoading} type="primary">
                Set execution date
              </Button>
            </td>
          </tr>
          <tr>
            <TdWithPadding colSpan={2}>
              <Title>Remove production</Title>
            </TdWithPadding>
          </tr>
          <tr>
            <td>
              {calculationData?.isPap ? (
                <Alert
                  message={`This calculation is exported to ${YPICK_LABEL}, so Production flag cannot be removed.`}
                  type="warning"
                  showIcon
                />
              ) : (
                <Text>
                  Calculation &#39;{calculationData?.title}&#39; will not be marked as
                  &#39;Production&#39; anymore.
                </Text>
              )}
            </td>
            <td>
              <Button
                onClick={removeProductionHandler}
                disabled={isLoading || calculationData?.isPap}
                $colorVariant="danger"
              >
                Remove production
              </Button>
            </td>
          </tr>
        </tbody>
      </table>
    </Modal>
  );
};

export default UnsetProductionModal;
